import React from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  Shield,
  Clock,
  BadgeCheck,
  CircleCheck,
  Fingerprint,
  Lock,
  Users,
  Bell,
  Smartphone,
} from "lucide-react";
import { Link } from "react-router-dom";
const CallToAction = () => {
  const benefits = [
    {
      icon: <Fingerprint size={20} className="text-blue-500" />,
      text: "Simple ID verification for your safety",
    },
    {
      icon: <Lock size={20} className="text-indigo-600" />,
      text: "Bank-grade security to protect your money",
    },
    {
      icon: <Shield size={20} className="text-purple-500" />,
      text: "Extra security steps to prevent fraud",
    },
    {
      icon: <Smartphone size={20} className="text-sky-500" />,
      text: "Easy-to-use mobile app",
    },
    {
      icon: <Users size={20} className="text-teal-500" />,
      text: "Fair and open bidding system",
    },
    {
      icon: <Bell size={20} className="text-amber-500" />,
      text: "Always-available customer support",
    },
  ];
  return (
    <section className="py-24 px-4 relative overflow-hidden font-sans">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 my-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 border-2 border-amber-400 text-white text-sm font-bold shadow-lg transform hover:scale-105 transition-all duration-300">
            <Clock size={16} className="mr-2 animate-pulse" />
            Launching Soon — Limited Early Access Available
          </div>

          <h2 className="text-3xl md:text-5xl font-bold mb-2 lg:leading-[1.8] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Experience the Future of Digital Committee
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Secure, transparent, and built with cutting-edge technology. Join
            thousands already on our waitlist.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* App mockup with 3D effect - MADE SMALLER */}
          <div className="lg:order-2 relative mx-auto max-w-xs md:max-w-sm lg:max-w-md">
            <div
              className="bg-white p-4 rounded-3xl shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500"
              style={{
                boxShadow:
                  "0 20px 80px -20px rgba(0, 0, 150, 0.1), 0 30px 60px -30px rgba(0, 0, 150, 0.2)",
                transformStyle: "preserve-3d",
              }}
            >
              {/* Mockup frame */}
              <div
                className="aspect-[9/16] bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl overflow-hidden border border-gray-200"
                style={{ transform: "translateZ(20px)" }}
              >
                <div className="h-10 bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                  <div className="w-1/4 h-1 bg-white/20 rounded-full"></div>
                </div>

                {/* App content mockup */}
                <div className="p-3">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <div className="h-2 w-20 bg-gray-300 rounded-full mb-1.5"></div>
                      <div className="h-1.5 w-14 bg-gray-200 rounded-full"></div>
                    </div>
                    <div className="h-6 w-6 bg-blue-100 rounded-full"></div>
                  </div>

                  {/* Main content */}
                  <div className="mb-4">
                    <div className="h-2.5 w-full bg-gray-200 rounded-full mb-3"></div>
                    <div className="h-2.5 w-5/6 bg-gray-200 rounded-full mb-3"></div>
                    <div className="h-2.5 w-4/6 bg-gray-200 rounded-full"></div>
                  </div>

                  {/* Stats graph */}
                  <div className="bg-white rounded-xl p-2 mb-4 shadow-sm">
                    <div className="h-1.5 w-1/3 bg-gray-200 rounded-full mb-2"></div>
                    <div className="h-16 w-full flex items-end space-x-0.5">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-blue-400 to-indigo-500 rounded-t-sm"
                          style={{ height: `${20 + Math.random() * 80}%` }}
                        ></div>
                      ))}
                    </div>
                  </div>

                  {/* Button */}
                  <div className="h-8 w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl"></div>
                </div>
              </div>
            </div>

            {/* Background glow */}
            <div className="absolute -bottom-8 w-full h-16 bg-blue-500/10 blur-3xl rounded-full"></div>

            {/* Floating cards with HIGHER Z-INDEX */}
            <div
              className="absolute -left-12 top-1/4 bg-white shadow-xl rounded-xl p-3 animate-float z-30"
              style={{ animationDuration: "6s", transform: "translateZ(40px)" }}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mr-2">
                  <CheckCircle2 size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Trusted by</p>
                  <p className="text-sm font-bold text-gray-800">
                    15,000+ users
                  </p>
                </div>
              </div>
            </div>

            <div
              className="absolute -right-12 bottom-1/4 bg-white shadow-xl rounded-xl p-3 animate-float z-30"
              style={{
                animationDuration: "8s",
                animationDelay: "1s",
                transform: "translateZ(40px)",
              }}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mr-2">
                  <BadgeCheck size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Security Rating</p>
                  <p className="text-sm font-bold text-gray-800">
                    A+ Certified
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Text content */}
          <div className="lg:order-1">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6">
              A{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  safe and secure
                </span>
                <span className="absolute bottom-0 left-0 w-full h-3 bg-blue-200/30 -z-10 rounded"></span>
              </span>{" "}
              experience for your finances
            </h2>

            <p className="text-lg text-gray-600 mb-8">
              Our platform employs state-of-the-art security measures and
              blockchain technology to ensure your investments are protected.
              Join a community of verified users and start growing your savings
              with confidence.
            </p>

            <div className="grid md:grid-cols-2 gap-x-6 gap-y-4 mb-12">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start group">
                  <div className="mr-3 mt-1 p-1 rounded-full bg-gray-50 group-hover:bg-blue-50 transition-colors duration-300">
                    {benefit.icon}
                  </div>
                  <p className="text-gray-700">{benefit.text}</p>
                </div>
              ))}
            </div>

            <div className="space-y-4 sm:space-y-0 sm:flex sm:space-x-4">
              <Button
                size="lg"
                className="w-full sm:w-auto text-base font-medium shadow-lg group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 rounded-xl transition-all duration-300"
                asChild
              >
                <Link
                  to="/waitlist"
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <Download size={18} className="mr-2" />
                  Join Waitlist
                  <ArrowRight
                    size={16}
                    className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto text-base font-medium border-2 border-gray-200 text-gray-700 hover:bg-gray-50 px-8 py-6 rounded-xl transition-all duration-300"
                asChild
              >
                <Link to="/learn-more" className="flex items-center">
                  Learn More
                </Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-8 flex items-center text-sm text-gray-500">
              <div className="flex -space-x-1 mr-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-6 h-6 rounded-full border-2 border-white"
                    style={{
                      backgroundColor: [
                        "#E0E7FF",
                        "#C7D2FE",
                        "#A5B4FC",
                        "#818CF8",
                      ][i],
                      zIndex: 4 - i,
                    }}
                  ></div>
                ))}
              </div>
              <p>
                <span className="font-medium">250+ early users</span> already on
                the waitlist
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;
